import React from 'react';
import Header from './HeaderHome';
import Footer from './FooterHome';

const CommunityService = () => {
  // ── Key Programmes data ──
  const programmes = [
    {
      icon: "🚑",
      title: "Disaster Relief & Emergency Response",
      desc: "Providing immediate relief—food, water, shelter, and medical aid—during natural disasters, accidents, and emergencies to affected communities.",
    },
    {
      icon: "🤝",
      title: "Social Welfare Programmes",
      desc: "Organising food distribution drives, clothing donations, and essential supply camps for homeless, destitute, and marginalised populations.",
    },
    {
      icon: "🏥",
      title: "Community Health Camps",
      desc: "Setting up free health check-up camps in urban slums and rural areas, offering screenings, consultations, and referrals for chronic conditions.",
    },
    {
      icon: "📢",
      title: "Awareness Campaigns",
      desc: "Conducting campaigns on hygiene, sanitation, legal rights, women's safety, and government schemes to empower communities with knowledge.",
    },
    {
      icon: "👫",
      title: "Youth Engagement & Empowerment",
      desc: "Engaging young people through leadership training, volunteer opportunities, and community projects to foster social responsibility and civic pride.",
    },
    {
      icon: "🏡",
      title: "Rural Development",
      desc: "Supporting rural communities with infrastructure improvements, access to clean water, sanitation facilities, and sustainable livelihood programmes.",
    },
  ];

  // ── Impact Stats ──
  const stats = [
    { number: "10,000+", label: "Beneficiaries Reached" },
    { number: "500+", label: "Relief Operations" },
    { number: "200+", label: "Awareness Campaigns" },
    { number: "1,000+", label: "Active Volunteers" },
  ];

  // ── How You Can Help ──
  const helpOptions = [
    { icon: "🤝", title: "Volunteer", desc: "Join our team and make a direct impact in communities." },
    { icon: "❤️", title: "Donate", desc: "Fund relief materials, food supplies, and community projects." },
    { icon: "🏢", title: "Partner", desc: "Collaborate with us to extend our reach and resources." },
  ];

  return (
    <>
      <Header />

      {/* ========== HERO ========== */}
      <section className="community-hero relative text-white py-16">
        <div className="container mx-auto px-4 text-center">
          <div className="max-w-3xl mx-auto">
            <div className="inline-block bg-primary/80 backdrop-blur-sm px-6 py-2 rounded-full text-sm font-medium mb-4">
              <i className="fas fa-hands-helping mr-2"></i> Our Service
            </div>
            <h1 className="text-4xl md:text-6xl font-bold leading-tight mb-4">
              Community Service
            </h1>
            <p className="text-xl md:text-2xl text-yellow-100/90 font-light">
              Building stronger, resilient, and self-reliant communities through collective action and compassion
            </p>
            <div className="w-24 h-1 bg-yellow-300 mx-auto mt-6 rounded-full"></div>
          </div>
        </div>
      </section>

      {/* ========== OVERVIEW ========== */}
      <section className="py-16">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto bg-white rounded-2xl shadow-xl p-8 md:p-12">
            <div className="text-center mb-10">
              <h2 className="text-3xl md:text-4xl font-bold text-gray-800">
                Serving Communities, Transforming Lives
              </h2>
              <div className="w-20 h-1 bg-primary mx-auto mt-3 rounded-full"></div>
            </div>
            <p className="text-gray-700 text-lg leading-relaxed mb-6">
              Strong communities are the foundation of a healthy society. Yet, many communities face challenges
              like poverty, lack of access to basic services, and social exclusion. At Venkatadri Seva Trust, our
              <strong>Community Service</strong> programme is dedicated to empowering communities by addressing
              their immediate needs and building long-term resilience.
            </p>
            <p className="text-gray-700 text-lg leading-relaxed">
              We work closely with local residents, government agencies, and other stakeholders to implement
              sustainable solutions—from disaster relief and health camps to awareness drives and youth
              engagement. Our approach is participative and inclusive, ensuring that communities take ownership
              of their own development.
            </p>
          </div>
        </div>
      </section>

      {/* ========== VISION & MISSION ========== */}
      <section className="py-16 bg-primary">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-5xl mx-auto">
            <div className="vision-card rounded-2xl p-8 text-white text-center">
              <div className="text-5xl mb-4">👁️</div>
              <h3 className="text-2xl font-bold mb-3">Our Vision</h3>
              <p className="text-yellow-50/90 text-lg leading-relaxed">
                Vibrant, self‑reliant communities where every individual has access to opportunities, resources,
                and a supportive network to thrive and contribute to society.
              </p>
            </div>
            <div className="vision-card rounded-2xl p-8 text-white text-center">
              <div className="text-5xl mb-4">🎯</div>
              <h3 className="text-2xl font-bold mb-3">Our Mission</h3>
              <p className="text-yellow-50/90 text-lg leading-relaxed">
                To mobilise volunteers, resources, and partnerships to address critical community needs—providing
                relief, promoting health, spreading awareness, and fostering social cohesion.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* ========== KEY PROGRAMMES ========== */}
      <section className="py-16">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-white">Our Key Programmes</h2>
            <p className="text-yellow-100 mt-2 text-lg">
              Comprehensive initiatives to strengthen communities from the ground up
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 max-w-6xl mx-auto">
            {programmes.map((programme, idx) => (
              <div
                key={idx}
                className="program-card bg-white p-6 rounded-lg shadow-md hover:shadow-xl transition-smooth text-center border-t-4 border-primary"
              >
                <div className="service-icon"><span>{programme.icon}</span></div>
                <h3 className="text-xl font-semibold text-gray-800">{programme.title}</h3>
                <p className="text-gray-600 mt-2 text-sm">{programme.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ========== IMPACT / STATS ========== */}
      {/* <section className="py-16 bg-primary">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-white">Our Impact</h2>
            <div className="w-20 h-1 bg-yellow-300 mx-auto mt-3 rounded-full"></div>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6 max-w-4xl mx-auto text-center text-white">
            {stats.map((stat, idx) => (
              <div key={idx}>
                <div className="text-4xl md:text-5xl font-bold text-yellow-300">{stat.number}</div>
                <p className="text-yellow-100/80 text-sm mt-1">{stat.label}</p>
              </div>
            ))}
          </div>
        </div>
      </section> */}

      {/* ========== HOW YOU CAN HELP ========== */}
      <section className="py-16">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto bg-white rounded-2xl shadow-xl p-8 md:p-12">
            <div className="text-center mb-8">
              <h2 className="text-3xl md:text-4xl font-bold text-gray-800">How You Can Help</h2>
              <div className="w-20 h-1 bg-primary mx-auto mt-3 rounded-full"></div>
              <p className="text-gray-600 mt-4 text-lg">
                Your contribution—whether time, money, or skills—can strengthen communities.
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {helpOptions.map((option, idx) => (
                <div key={idx} className="text-center p-4 rounded-lg bg-gray-50 hover:bg-yellow-50 transition-smooth">
                  <div className="text-4xl text-primary mb-3">{option.icon}</div>
                  <h4 className="font-bold text-gray-800">{option.title}</h4>
                  <p className="text-gray-600 text-sm mt-1">{option.desc}</p>
                </div>
              ))}
            </div>

            <div className="text-center mt-8">
              <a
                href="/donate"
                className="bg-primary text-white px-8 py-3 rounded-lg font-semibold hover:bg-primary-dark transition-smooth inline-block"
              >
                <i className="fas fa-hand-holding-heart mr-2"></i> Support Community Service
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* ========== TESTIMONIAL ========== */}
      <section className="py-16 bg-primary">
        <div className="container mx-auto px-4">
          <div className="max-w-3xl mx-auto text-center text-white">
            <div className="text-6xl mb-4">“</div>
            <p className="text-xl md:text-2xl font-light leading-relaxed italic">
              After the floods, we had nothing—no food, no shelter. Venkatadri Seva Trust arrived within hours
              with relief supplies and helped us rebuild our lives. They didn't just give us aid; they gave us
              hope and a sense of community again.
            </p>
            <div className="mt-6">
              <p className="font-semibold text-yellow-200">— Gopal, 45</p>
              <p className="text-yellow-100/70 text-sm">Flood‑affected area</p>
            </div>
          </div>
        </div>
      </section>

      {/* ========== JOIN OUR MISSION ========== */}
      <section className="py-16">
        <div className="container mx-auto px-4 text-center text-white">
          <div className="max-w-3xl mx-auto">
            <h2 className="text-3xl md:text-4xl font-bold mb-4">Join Our Mission</h2>
            <div className="w-20 h-1 bg-yellow-300 mx-auto mb-6 rounded-full"></div>
            <p className="text-xl text-yellow-100/90 italic mb-6">
              "Alone we can do so little; together we can do so much."
            </p>
            <p className="text-lg text-white/80 leading-relaxed mb-8">
              Your generous support—through donations, volunteering, or partnerships—helps us reach more people,
              respond to crises faster, and build stronger, more resilient communities across the nation.
            </p>
            <p className="text-xl text-yellow-100 font-light mb-8">
              Together, let us create a world where no one is left behind—where communities unite to lift each
              other up.
            </p>
            <div className="flex flex-wrap justify-center gap-4">
              <a
                href="/donate"
                className="bg-white text-primary px-8 py-3 rounded-lg font-semibold hover:bg-gray-100 transition-smooth"
              >
                <i className="fas fa-heart mr-2"></i> Donate Now
              </a>
              <a
                href="/volunteer"
                className="border-2 border-white text-white px-8 py-3 rounded-lg font-semibold hover:bg-white/10 transition-smooth"
              >
                <i className="fas fa-hand-peace mr-2"></i> Volunteer
              </a>

            </div>
          </div>
        </div>
      </section>

      <Footer />
    </>
  );
};

export default CommunityService;